import { useMemo } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import DashboardKPITile from '@/components/DashboardKPITile';
import { aud } from '@/utils/format';
import { toCsv, downloadCsv } from '@/utils/csv';
import type { StudioId } from '@/domain/types';
import {
  weeklyCompletedTally,
  depositsReceivedIn,
  newLeadsByDay,
  rebookRate,
  agentPerformance,
  unworkedLeads,
} from '@/domain/reportingEngine';
import { opsOverdueTopups } from '@/domain/opsReports';

interface Props {
  days: readonly string[];
  studioId?: StudioId;
}

/**
 * Phase-1 reporting summary (tv-3u8): the week at a glance (completed, deposits,
 * leads, rebook rate), a money-at-risk hero (overdue top-ups · tv-dwu), what's
 * slipping (unworked leads · tv-955), and per-agent activity vs. target
 * (tv-pa3). Surfaced on the existing reporting dashboard — the "surface the
 * dashboard" default. (The targets auto-derive-at-5-and-lock rule · tv-za4 is a
 * targeted domain follow-on; the standalone Targets tab covers targets today.)
 */
export default function Phase1Tab({ days, studioId }: Props) {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const topupPlans = useBrainStore((s) => s.topupPlans);

  /* eslint-disable react-hooks/exhaustive-deps */
  const completed = useMemo(() => weeklyCompletedTally(days, studioId), [days, studioId, bookings]);
  // Deposits received = the ledger's money in (the Day sheet's rule; 9mb8): one
  // definition on every money surface.
  const depositsAud = useMemo(() => depositsReceivedIn(days, studioId).totalAud, [days, studioId, deposits]);
  const leads = useMemo(
    () => newLeadsByDay(days, studioId).reduce((s, d) => s + d.count, 0),
    [days, studioId],
  );
  const rr = useMemo(() => rebookRate(days, studioId), [days, studioId, bookings]);
  const agents = useMemo(() => agentPerformance(days, studioId), [days, studioId, bookings]);
  /* eslint-enable react-hooks/exhaustive-deps */
  const atRisk = useMemo(
    () => opsOverdueTopups({ nowIso: clockIso, topupPlans, studioIds: studioId ? [studioId] : [] }),
    [clockIso, topupPlans, studioId],
  );
  const slipping = useMemo(() => unworkedLeads(studioId), [studioId]);

  return (
    <div className="space-y-6" data-testid="reporting-phase1">
      {/* Weekly summary tiles (tv-362) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <DashboardKPITile label="Completed" value={completed.toString()} caption="sessions, window" />
        <DashboardKPITile label="Deposits paid" value={aud(depositsAud)} caption="in window" />
        <DashboardKPITile label="New leads" value={leads.toString()} caption="in window" />
        <DashboardKPITile
          label="Rebook rate"
          value={`${Math.round(rr.rate * 100)}%`}
          caption={`${rr.rebooks}/${rr.completed}`}
        />
      </div>

      {/* Money-at-risk hero (tv-dwu) + what's slipping (tv-955) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="panel p-5 border-l-4 border-blood" data-testid="phase1-money-at-risk">
          <p className="label-small-caps text-blood">Money at risk this week</p>
          <p className="heading-display text-3xl text-cream mt-1">{aud(atRisk.totalOutstandingAud)}</p>
          <p className="font-inter text-xs text-cream-soft mt-1">
            {atRisk.count} overdue top-up{atRisk.count === 1 ? '' : 's'} the desk hasn't cleared.
          </p>
        </div>
        <div className="panel p-5" data-testid="phase1-slipping">
          <p className="label-small-caps text-gold-bright">What&apos;s slipping</p>
          <p className="heading-display text-3xl text-cream mt-1">{slipping.count}</p>
          <p className="font-inter text-xs text-cream-soft mt-1">
            Unworked leads{slipping.oldestIso ? ` · oldest since ${slipping.oldestIso.slice(0, 10)}` : ''}.
          </p>
        </div>
      </div>

      {/* Per-agent activity vs. target (tv-pa3) */}
      <div className="panel p-5" data-testid="phase1-agents">
        <div className="flex items-center justify-between mb-2">
          <p className="label-small-caps text-cream-soft">Per-agent activity</p>
          {agents.length > 0 ? (
            <button
              type="button"
              data-testid="phase1-export-csv"
              onClick={() =>
                downloadCsv(
                  'ops-per-agent.csv',
                  toCsv(
                    ['Agent', 'Outbound', 'Inbound', 'Deposits', 'Conversion'],
                    agents.map((a) => [
                      a.name,
                      a.outboundCalls,
                      a.inboundCalls,
                      a.deposits,
                      a.conversion === null ? '' : `${Math.round(a.conversion * 100)}%`,
                    ]),
                  ),
                )
              }
              className="rounded-md border border-gold-dim/40 px-2 py-1 text-[11px] uppercase tracking-widest text-cream-soft hover:border-gold-bright/50 transition"
            >
              Download CSV
            </button>
          ) : null}
        </div>
        {agents.length === 0 ? (
          <p className="font-serif text-cream-soft text-sm">No agent activity in the window.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[420px]">
              <thead>
                <tr className="text-cream-faint text-left text-[11px] uppercase tracking-wide">
                  <th className="font-normal pb-1">Agent</th>
                  <th className="font-normal pb-1 text-right">Out</th>
                  <th className="font-normal pb-1 text-right">In</th>
                  <th className="font-normal pb-1 text-right">Deposits</th>
                  <th className="font-normal pb-1 text-right">Conv.</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <tr key={a.agentId} className="border-t border-gold-dim/15">
                    <td className="py-1 text-cream">{a.name}</td>
                    <td className="py-1 text-right font-mono text-cream-soft">{a.outboundCalls}</td>
                    <td className="py-1 text-right font-mono text-cream-soft">{a.inboundCalls}</td>
                    <td className="py-1 text-right font-mono text-cream-soft">{a.deposits}</td>
                    <td className="py-1 text-right font-mono text-cream-soft">
                      {a.conversion === null ? '—' : `${Math.round(a.conversion * 100)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
